import PauseRounded from '@mui/icons-material/PauseRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import { tipChrome } from '@/content/tips';

type Props = {
  index: number;
  total: number;
  /** The visitor's own pause decision, not whether it happens to be still. */
  paused: boolean;
  onGoTo: (index: number) => void;
  onTogglePaused: () => void;
};

/**
 * How wide the current slide's dot is. The others are square.
 *
 * The active dot is a stadium rather than a bigger circle because size alone
 * is a weak signal at 8px and a brand-red pill against two grey dots is
 * unmistakable at a glance. Both the colour *and* the shape change, so the
 * current slide is not indicated by colour alone (WCAG 1.4.1) — which matters
 * here, since the inactive colour is a grey of the same accent family in dark
 * mode.
 */
const DOT_ACTIVE_WIDTH = 18;
const DOT_SIZE = 8;

/**
 * The deck's footer: where you are in it, and the way to stop it moving.
 *
 * ## Why there is a pause button at all
 *
 * The deck turns itself over every seven seconds, and content that moves on
 * its own for longer than five seconds has to be pausable (WCAG 2.2.2). The
 * pause button is how this feature meets that, and it is the reason the
 * rotation is allowed to exist — without it the only compliant deck is one the
 * visitor has to drive, which in practice means a deck whose second and third
 * slides nobody ever reads.
 *
 * Its name says what pressing it *does* ("Stop the tips changing"), not what
 * state the deck is in, because a screen reader user hears the name and has to
 * be able to predict the outcome. The icon flips with it; the icon alone is
 * not the affordance.
 *
 * ## Why the dots are buttons
 *
 * They are the only way back to a slide that has already gone past, and they
 * have to be reachable from the keyboard like every other control (WCAG
 * 2.1.1). So: real `ButtonBase` elements with their own names ("Show tip 2 of
 * 3"), 24px square tap targets around an 8px mark (WCAG 2.5.8), and `-mx` on
 * the row so those targets can be full size without the dots themselves
 * drifting away from the card's text column.
 *
 * Pressing one stops the rotation for good, which is handled in
 * `hooks/useTipCarousel.ts` rather than here — a visitor who has taken the
 * wheel keeps it.
 */
export function TipDeckControls({ index, total, paused, onGoTo, onTogglePaused }: Props) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      {/* The dots carry their own position in their names, so the row itself
          is presentational — a `tablist` would be the wrong role (these do not
          control panels that stay in the DOM) and a bare group adds a layer a
          screen reader has to walk past to reach three buttons. */}
      <Stack direction="row" alignItems="center" sx={{ mx: -1 }}>
        {Array.from({ length: total }, (_, at) => {
          const current = at === index;
          return (
            <ButtonBase
              key={at}
              onClick={() => onGoTo(at)}
              aria-label={tipChrome.goToSlide(at, total)}
              aria-current={current ? 'true' : undefined}
              sx={{
                /* 24px of target around an 8px mark. Laid out as a flex box
                   rather than with padding so the mark stays centred however
                   wide it grows when it becomes the current one. */
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: current ? DOT_ACTIVE_WIDTH : DOT_SIZE,
                  height: DOT_SIZE,
                  borderRadius: 999,
                  bgcolor: current ? 'primary.main' : 'action.disabled',
                  /* The one transition on this control. The dots are the only
                     part of the footer that changes as the deck turns, and a
                     mark that grows into place reads as the same object moving
                     along the row rather than three marks being repainted.
                     Covered by the theme's global reduced-motion clamp. */
                  transition: (t) =>
                    t.transitions.create(['width', 'background-color'], { duration: 200 }),
                }}
              />
            </ButtonBase>
          );
        })}
      </Stack>

      <IconButton
        onClick={onTogglePaused}
        aria-label={paused ? tipChrome.play : tipChrome.pause}
        /* `mr: -2` for the same reason the dismiss button has it: the theme
           floors every icon button at the 44px touch target, and pulling the
           surplus out of the padding lines the glyph up with the card's text
           column instead of with its own tap area. */
        sx={{ flexShrink: 0, mr: -2, color: 'text.secondary' }}
      >
        {paused ? <PlayArrowRounded fontSize="small" /> : <PauseRounded fontSize="small" />}
      </IconButton>
    </Stack>
  );
}
